import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { FiMail, FiMessageSquare, FiAlertCircle, FiCheckCircle, FiSend, FiArrowLeft } from 'react-icons/fi';
import { Link } from 'react-router-dom';

const ISSUE_TYPES = [
  { id: 'bug', label: 'Bug Report', description: 'Something isn\'t working correctly' },
  { id: 'feature', label: 'Feature Request', description: 'Suggest a new feature or improvement' },
  { id: 'billing', label: 'Billing Question', description: 'Questions about plans, credits, or payments' },
  { id: 'account', label: 'Account Issue', description: 'Login problems, settings, or data concerns' },
  { id: 'other', label: 'Other', description: 'General questions or feedback' }
];

function ContactSupport() {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    issueType: '',
    subject: '',
    message: '',
    email: user?.email || ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      await api.submitSupportRequest({
        ...formData,
        user_id: user?.user_id,
        subscription_tier: user?.subscription_tier || 'free'
      });
      setSubmitted(true);
    } catch (err) {
      console.error('Error submitting support request:', err);
      setError('Failed to submit your request. Please try again or email us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  // Determine priority based on tier
  const getPriorityInfo = () => {
    const tier = user?.subscription_tier || 'free';
    const priorityTiers = ['growth', 'scale'];
    const isPriority = priorityTiers.includes(tier);
    
    return {
      isPriority,
      responseTime: isPriority ? '4-12 hours' : '24-48 hours',
      tierLabel: tier.charAt(0).toUpperCase() + tier.slice(1)
    };
  };

  const priorityInfo = getPriorityInfo();

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto animate-fade-in-up">
        <div className="bg-card rounded-2xl border border-border/50 p-8 text-center">
          <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <FiCheckCircle className="text-emerald-400" size={32} />
          </div>
          <h2 className="text-2xl font-heading font-bold text-foreground mb-2">Request Submitted!</h2>
          <p className="text-muted-foreground mb-6">
            We have received your message and will get back to you within{' '}
            <strong className="text-foreground">{priorityInfo.responseTime}</strong>.
          </p>
          {priorityInfo.isPriority && (
            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4 mb-6">
              <p className="text-accent text-sm">
                <strong>Priority Support Active</strong> - As a {priorityInfo.tierLabel} member, 
                your request has been marked as priority.
              </p>
            </div>
          )}
          <div className="flex justify-center gap-4">
            <Link
              to="/help"
              className="px-4 py-2 text-muted-foreground hover:text-foreground font-medium transition-colors"
            >
              Back to Help Center
            </Link>
            <Link
              to="/dashboard"
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 font-medium transition-colors"
            >
              Go to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto animate-fade-in-up">
      <Link to="/help" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors">
        <FiArrowLeft size={16} />
        Back to Help Center
      </Link>

      <div className="bg-card rounded-2xl border border-border/50 overflow-hidden">
        <div className="p-6 bg-gradient-to-r from-primary to-accent">
          <h1 className="text-2xl font-heading font-bold text-white mb-1">Contact Support</h1>
          <p className="text-white/80">We are here to help you succeed with FirstCut</p>
        </div>

        {/* Priority Badge */}
        {priorityInfo.isPriority && (
          <div className="bg-accent/10 border-b border-accent/20 px-6 py-3 flex items-center gap-2">
            <span className="px-2 py-0.5 bg-accent text-accent-foreground text-xs font-bold rounded">PRIORITY</span>
            <span className="text-accent text-sm">
              {priorityInfo.tierLabel} plan - Expected response within {priorityInfo.responseTime}
            </span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Your Email
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-2.5 bg-secondary border border-border/50 rounded-lg text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
              placeholder="you@example.com"
              data-testid="contact-email"
            />
          </div>

          {/* Issue Type */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              What can we help you with?
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {ISSUE_TYPES.map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, issueType: type.id })}
                  className={`p-3 rounded-lg border-2 text-left transition-all ${
                    formData.issueType === type.id
                      ? 'border-primary bg-primary/10'
                      : 'border-border/50 hover:border-primary/30 bg-secondary/50'
                  }`}
                  data-testid={`issue-type-${type.id}`}
                >
                  <div className="font-medium text-foreground text-sm">{type.label}</div>
                  <div className="text-xs text-muted-foreground">{type.description}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Subject */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Subject
            </label>
            <input
              type="text"
              required
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              className="w-full px-4 py-2.5 bg-secondary border border-border/50 rounded-lg text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all"
              placeholder="Brief description of your issue"
              data-testid="contact-subject"
            />
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Message
            </label>
            <textarea
              required
              rows={5}
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full px-4 py-2.5 bg-secondary border border-border/50 rounded-lg text-foreground placeholder:text-muted-foreground focus:ring-2 focus:ring-primary/50 focus:border-primary/50 transition-all resize-none"
              placeholder="Please describe your issue or question in detail..."
              data-testid="contact-message"
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="flex items-center gap-2 p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400">
              <FiAlertCircle size={20} />
              <span>{error}</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={submitting || !formData.issueType}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:bg-secondary disabled:text-muted-foreground disabled:cursor-not-allowed font-medium transition-colors"
            data-testid="contact-submit"
          >
            {submitting ? (
              <>
                <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin" />
                Submitting...
              </>
            ) : (
              <>
                <FiSend size={18} />
                Submit Request
              </>
            )}
          </button>

          {/* Alternative Contact */}
          <div className="pt-4 border-t border-border">
            <p className="text-sm text-muted-foreground text-center">
              Prefer email? Reach us directly at{' '}
              <a href="mailto:support@firstcut.app" className="text-primary hover:underline">
                support@firstcut.app
              </a>
            </p>
          </div>
        </form>
      </div>

      {/* Response Time Info */}
      <div className="mt-6 p-4 bg-secondary rounded-xl">
        <h3 className="font-medium text-foreground mb-2 flex items-center gap-2">
          <FiMessageSquare size={18} />
          Response Times
        </h3>
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-muted-foreground">Free / Solo / Pro</div>
            <div className="font-medium text-foreground">24-48 hours</div>
          </div>
          <div>
            <div className="text-muted-foreground">Starter</div>
            <div className="font-medium text-foreground">12-24 hours</div>
          </div>
          <div>
            <div className="text-muted-foreground">Growth / Scale</div>
            <div className="font-medium text-primary">4-12 hours (Priority)</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ContactSupport;
